import { transport } from '@opengov/app-core/transport';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ISettings, IEntityStorySettingsGraphqlResponse } from '../../../../types';
import { updateSettings as updateSettingsQuery } from '../../../graphQL/queries';
import { processEntitySettings, processPutColors, processPutSettings } from '../../../transformations/settingsTransformation';
import { storiesSettingsQueryKeys } from './storiesSettingsQueryKeys';
import { useToast } from '../../Toast/context/useToast.context';

export type UpdateSettingsParams = {
  settings: ISettings;
  entityId: number;
  entitySubdomain: string;
};

export type UseUpdateSettingsOptions = {
  onSuccess?: (settings: ISettings, params: UpdateSettingsParams) => void;
};

export function useUpdateSettings({ onSuccess }: UseUpdateSettingsOptions = {}) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  const { mutate: updateSettings, isLoading: isUpdating } = useMutation({
    mutationFn: async (params: UpdateSettingsParams): Promise<ISettings> => {
      const { settings, entityId } = params;

      const newSettings = processPutSettings(settings);
      const { bodyBackground, bodyFont, headerBackground, headerFont, footerBackground, footerFont } =
        processPutColors(settings);

      const response: { data: IEntityStorySettingsGraphqlResponse } = await transport
        .url(`/api/data/objects/stories/v1/graphql`)
        .json({
          query: updateSettingsQuery,
          variables: {
            entityId,
            newSettings,
            bodyBackground,
            bodyFont,
            headerBackground,
            headerFont,
            footerBackground,
            footerFont
          }
        })
        .post()
        .json();

      if (!response.data) {
        return Promise.reject(response);
      }

      return processEntitySettings(response.data);
    },

    onSuccess: (updatedSettings, params) => {
      queryClient.setQueryData(
        storiesSettingsQueryKeys.settings(params.entityId),
        updatedSettings
      );
      onSuccess?.(updatedSettings, params);
    },

    onError: () => {
      toggleToast({
        title: 'Request Error',
        body: 'Could not update settings',
        severity: 'error'
      });
    }
  });

  return { updateSettings, isUpdating };
}
